import React from 'react';
import { Wrench, CheckCircle2, Timer, User, Wifi, WifiOff, Gauge } from 'lucide-react';
import {
  RIG_STATUS_META,
  effectiveRigStatus,
  rigHeartbeat,
  sessionRemainingMs,
  formatCountdown,
} from '@/lib/kiosk';

export default function RigFleetCard({ rig, feeds, onSanitise, onLogFault, busy }) {
  const status = effectiveRigStatus(rig, feeds);
  const meta = RIG_STATUS_META[status] || RIG_STATUS_META.READY;
  const { feed, online } = rigHeartbeat(rig, feeds);
  const remaining = sessionRemainingMs(rig);

  return (
    <div className={`border bg-[#111115] p-4 flex flex-col ${meta.frame}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-white font-black text-lg leading-none">{rig.bay_number}</p>
          <p className="text-white/50 text-[11px] font-bold tracking-widest mt-1.5 uppercase truncate">{rig.name}</p>
        </div>
        <span className={`shrink-0 flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-bold tracking-widest ${meta.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
      </div>

      <div className="flex items-center gap-1.5 mt-3">
        {online ? <Wifi className="w-3.5 h-3.5 text-green-400" /> : <WifiOff className="w-3.5 h-3.5 text-white/25" />}
        <span className="text-white/40 text-[10px] font-mono font-bold tracking-wider">
          {rig.telemetry_slot_id || 'NO SLOT'} · {online ? 'ONLINE' : 'OFFLINE'}
        </span>
      </div>

      {status === 'LIVE' && (
        <div className="mt-3 border border-blue-500/30 bg-blue-500/5 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-white text-sm font-bold truncate">
            <User className="w-3.5 h-3.5 text-blue-400 shrink-0" /> {rig.current_driver || 'Driver'}
          </p>
          <p className="flex items-center gap-1.5 text-blue-300 font-mono font-black text-xl mt-1.5 tabular-nums">
            <Timer className="w-4 h-4" /> {formatCountdown(remaining)}
          </p>
        </div>
      )}

      {/* A car on track with no booking behind it — the bay is occupied, so staff
          can see what is being driven rather than reading it as free. */}
      {status === 'IN_USE' && (
        <div className="mt-3 border border-cyan-500/30 bg-cyan-500/5 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-white text-sm font-bold truncate">
            <Gauge className="w-3.5 h-3.5 text-cyan-400 shrink-0" /> {feed?.driver_name || 'Car on track'}
          </p>
          <p className="text-cyan-300/80 text-[10px] font-bold tracking-widest uppercase mt-1.5 truncate">
            {feed?.track ? `On track · ${feed.track}` : 'Telemetry active'}
          </p>
        </div>
      )}

      <div className="mt-auto pt-3">
        {status === 'CLEANING_REQUIRED' && (
          <button
            onClick={() => onSanitise(rig)}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-xs tracking-widest py-4 mb-2 transition-colors"
          >
            <CheckCircle2 className="w-4 h-4" /> MARK SANITISED &amp; READY
          </button>
        )}
        <button
          onClick={() => onLogFault(rig)}
          className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-white/35 text-white/60 hover:text-white font-bold text-[11px] tracking-widest py-3 transition-colors"
        >
          <Wrench className="w-3.5 h-3.5" /> LOG FAULT
        </button>
      </div>
    </div>
  );
}